/**
 * Four hosts, sixteen gauges, and one that refuses to be a number.
 *
 * `nuc-lab` reports CPU, memory and disk and does not report a GPU. The whole
 * section is that one hatched purple bar sitting beside fifteen filled ones:
 * unmeasured has its own colour, its own texture and its own word, because an
 * empty bar and a zero bar look identical and mean opposite things.
 *
 * The rule is not in this file. `MeterFill` refuses to animate or fill a null,
 * so no figure can get this wrong by forgetting to — which matters more than
 * this figure being right once.
 *
 * One host's CPU moves every four seconds. One, not all: telemetry arriving
 * looks like a single reading updating, and everything moving at once looks like
 * a demo loop, which is what a reader assumes a marketing dashboard is.
 */
import { useEffect, useState } from 'react'

import { Panel, Badge, MeterRow } from '../../chrome'
import { MeterFill, useReducedMotionSafe } from '../../kit'
import { useT } from '../../i18n/context'
import { HOSTS } from '../../data/fixtures'
import './FleetBoard.css'

const GAUGES = [
  { key: 'cpu', label: 'fleet.fig.cpu' },
  { key: 'ram', label: 'fleet.fig.ram' },
  { key: 'disk', label: 'fleet.fig.disk' },
  { key: 'gpu', label: 'fleet.fig.gpu' },
] as const

export function FleetBoard() {
  const t = useT()
  const reduced = useReducedMotionSafe()
  // Which host's CPU is currently being re-read, and by how much.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (reduced) return
    const id = setInterval(() => setTick((n) => n + 1), 4000)
    return () => clearInterval(id)
  }, [reduced])

  const live = tick % HOSTS.length
  // A deterministic wobble rather than Math.random: the same page rendered twice
  // must produce the same bytes, or the prerender and the hydration disagree.
  const drift = ((tick * 7) % 11) / 100 - 0.05

  return (
    <div className="kl-fleet">
      {HOSTS.map((host, i) => (
        <Panel
          key={host.name}
          className="kl-fleet__host"
          title={<span className="k-mono">{host.name}</span>}
          actions={
            <Badge tone={host.state === 'online' ? 'ok' : 'warn'}>
              {host.state === 'online' ? 'online' : t('ui.behind')}
            </Badge>
          }
        >
          {GAUGES.map((g) => {
            const raw = host[g.key]
            const value =
              raw === null || g.key !== 'cpu' || i !== live
                ? raw
                : Math.max(0.02, Math.min(0.98, raw + drift))
            return (
              <MeterRow
                key={g.key}
                label={t(g.label)}
                value={
                  value === null ? (
                    <span className="kl-fleet__unmeasured">{t('ui.unmeasured')}</span>
                  ) : (
                    `${Math.round(value * 100)}%`
                  )
                }
              >
                <MeterFill
                  value={value}
                  label={`${host.name} ${t(g.label)}`}
                  unknownLabel={t('ui.unmeasured')}
                  tone={value !== null && value > 0.7 ? 'warn' : 'accent'}
                />
              </MeterRow>
            )
          })}
        </Panel>
      ))}
    </div>
  )
}

/**
 * What happens when each dependency goes down.
 *
 * Printed because it is the unusual part: most systems have this table only in
 * an incident review. No row of it says "serve the stale value as though it were
 * fresh", and that is the sentence the section makes about it.
 */
export function DegradationTable() {
  const t = useT()
  // The four rows are docs/ARCHITECTURE.md §14, not a plausible-sounding
  // paraphrase. The first draft of this figure invented them and got MQTT
  // backwards — the heartbeat falls back to the control-plane WebSocket rather
  // than stopping. Where the repository states a behaviour, it is the source.
  const rows = [
    ['mongodb', 'fleet.degr.mongodb'],
    ['rabbitmq', 'fleet.degr.rabbitmq'],
    ['redis', 'fleet.degr.redis'],
    ['mqtt', 'fleet.degr.mqtt'],
  ] as const

  return (
    <div className="kl-degr">
      <h3 className="kl-degr__title">{t('fleet.degradationTitle')}</h3>
      <p className="kl-degr__sub">{t('fleet.degradationSub')}</p>
      <table className="kl-degr__table">
        <tbody>
          {rows.map(([dep, effect]) => (
            <tr key={dep}>
              <th scope="row" className="k-mono">
                {dep}
              </th>
              <td>{t(effect)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
